import { drawBonuses, drawFog, drawShieldLine } from './bonusArt'
import { gameConfig, WORLD_HEIGHT, WORLD_WIDTH } from './config'
import { drawRacket, racketLean } from './racket'
import type { SkinColors } from './skins'
import { currentHalfWidth, type GameState } from './state'

/** Как логическое поле ложится на canvas: масштаб и поля по краям, в пикселях устройства. */
export interface View {
  width: number
  height: number
  scale: number
  offsetX: number
  offsetY: number
}

export const palette = {
  // Замерено по центру заливки зала в референсе (assets): rgb(205, 227, 247).
  floor: '#cde3f7',
  floorShade: '#bcd3ea',
  courtNear: '#7fa8d8',
  courtFar: '#9dbde3',
  line: '#ffffff',
  netMesh: 'rgba(44, 66, 102, 0.28)',
  netTape: '#ffffff',
  post: '#34507a',
  particle: '#ffffff',
} as const

/** Поле вписывается целиком и центрируется — баланс одинаков на любом экране. */
export function computeView(width: number, height: number): View {
  const scale = Math.min(width / WORLD_WIDTH, height / WORLD_HEIGHT)
  return {
    width,
    height,
    scale,
    offsetX: (width - WORLD_WIDTH * scale) / 2,
    offsetY: (height - WORLD_HEIGHT * scale) / 2,
  }
}

/** Экранная точка (px устройства) → координата x поля. */
export function screenToWorldX(view: View, px: number): number {
  return (px - view.offsetX) / view.scale
}

export function render(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  view: View,
  skin: SkinColors,
): void {
  // Пол, корт и сетка не меняются между кадрами — рисуются один раз в
  // оффскрин и дальше копируются: это и быстрее, и позволяет накладывать
  // текстуру покрытия, не тратя на неё время каждого кадра.
  const bg = getBackground(view)
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  if (bg) ctx.drawImage(bg, 0, 0)
  else {
    ctx.fillStyle = palette.floor
    ctx.fillRect(0, 0, view.width, view.height)
  }

  ctx.setTransform(view.scale, 0, 0, view.scale, view.offsetX, view.offsetY)
  if (state.wind) drawWind(ctx, state.wind.force, state.wind.left, state.elapsed)
  drawParticles(ctx, state)
  drawBonuses(ctx, state.bonuses)
  if (state.effects.shield) drawShieldLine(ctx, state.elapsed)
  drawRacket(
    ctx,
    state.paddle.x,
    gameConfig.paddle.y,
    currentHalfWidth(state),
    racketLean(state.paddle.vx),
    skin,
  )
  drawShuttle(ctx, state, skin)
  if (state.effects.fog > 0) drawFog(ctx, state.effects.fog, state.elapsed)

  ctx.setTransform(1, 0, 0, 1, 0, 0)
  drawVignette(ctx, view)
  drawGrain(ctx, view)
  if (state.slowMoLeft === null && state.effects.slow > 0) drawTint(ctx, view, SLOW_TINT)
  if (state.slowMoLeft === null && state.effects.fast > 0) drawTint(ctx, view, FAST_TINT)
  if (state.slowMoLeft !== null) drawMissShade(ctx, view, state.slowMoLeft)
}

// ─── Статичный фон: зал, корт, сетка ──────────────────────────────────────

let bgCache: { width: number; height: number; canvas: HTMLCanvasElement } | null = null

function getBackground(view: View): HTMLCanvasElement | null {
  if (bgCache && bgCache.width === view.width && bgCache.height === view.height) {
    return bgCache.canvas
  }
  const canvas = document.createElement('canvas')
  canvas.width = view.width
  canvas.height = view.height
  const g = canvas.getContext('2d')
  if (!g) return null
  g.setTransform(view.scale, 0, 0, view.scale, view.offsetX, view.offsetY)
  drawCourt(g)
  drawCourtTexture(g)
  drawHallLight(g)
  drawNet(g)
  bgCache = { width: view.width, height: view.height, canvas }
  return canvas
}

/** Шумовая плитка: крапинки покрытия и зерно кадра. Генерируется один раз. */
let noiseTile: HTMLCanvasElement | null = null

function getNoiseTile(): HTMLCanvasElement | null {
  if (noiseTile) return noiseTile
  const tile = document.createElement('canvas')
  tile.width = 128
  tile.height = 128
  const g = tile.getContext('2d')
  if (!g) return null
  for (let i = 0; i < 1100; i++) {
    const light = Math.random() > 0.45
    g.fillStyle = light
      ? `rgba(255, 255, 255, ${0.03 + Math.random() * 0.06})`
      : `rgba(22, 50, 79, ${0.02 + Math.random() * 0.05})`
    g.fillRect(Math.random() * 128, Math.random() * 128, 1, 1)
  }
  noiseTile = tile
  return tile
}

/** Тарафлекс: мелкий крап на покрытии корта — убирает «плоскую» заливку. */
function drawCourtTexture(ctx: CanvasRenderingContext2D): void {
  const tile = getNoiseTile()
  if (!tile) return
  const pattern = ctx.createPattern(tile, 'repeat')
  if (!pattern) return
  ctx.save()
  courtQuadPath(ctx)
  ctx.clip()
  ctx.fillStyle = pattern
  ctx.fillRect(-WORLD_WIDTH, -WORLD_HEIGHT, WORLD_WIDTH * 3, WORLD_HEIGHT * 3)
  ctx.restore()
}

/**
 * Свет зала: два тёплых пятна прожекторов на корте и тёмная трибуна сверху.
 * Без этого пол читается как бесконечная плоскость, а не как зал.
 */
function drawHallLight(ctx: CanvasRenderingContext2D): void {
  // Трибуны за дальним краем уходят в тень.
  const stands = ctx.createLinearGradient(0, -WORLD_HEIGHT, 0, WORLD_HEIGHT * 0.32)
  stands.addColorStop(0, 'rgba(22, 50, 79, 0.26)')
  stands.addColorStop(1, 'rgba(22, 50, 79, 0)')
  ctx.fillStyle = stands
  ctx.fillRect(-WORLD_WIDTH, -WORLD_HEIGHT, WORLD_WIDTH * 3, WORLD_HEIGHT * 1.4)

  for (const [cy, ry, alpha] of [
    [WORLD_HEIGHT * 0.62, 210, 0.1],
    [WORLD_HEIGHT * 0.22, 150, 0.07],
  ] as const) {
    const pool = ctx.createRadialGradient(WORLD_WIDTH / 2, cy, 0, WORLD_WIDTH / 2, cy, ry)
    pool.addColorStop(0, `rgba(255, 255, 255, ${alpha})`)
    pool.addColorStop(1, 'rgba(255, 255, 255, 0)')
    ctx.fillStyle = pool
    ctx.save()
    ctx.translate(WORLD_WIDTH / 2, cy)
    ctx.scale(1.6, 1)
    ctx.translate(-WORLD_WIDTH / 2, -cy)
    ctx.fillRect(WORLD_WIDTH / 2 - ry * 2, cy - ry, ry * 4, ry * 2)
    ctx.restore()
  }
}

/** Финальное зерно поверх кадра — «плёнка», смывающая цифровую чистоту. */
function drawGrain(ctx: CanvasRenderingContext2D, view: View): void {
  const tile = getNoiseTile()
  if (!tile) return
  const pattern = ctx.createPattern(tile, 'repeat')
  if (!pattern) return
  ctx.globalAlpha = 0.5
  ctx.fillStyle = pattern
  ctx.fillRect(0, 0, view.width, view.height)
  ctx.globalAlpha = 1
}

// ─── Корт в перспективе ───────────────────────────────────────────────────
//
// Вид из-за спины игрока: ближняя лицевая линия у нижнего края, сетка — у
// верхней трети. Волан «вверх по экрану» летит к сетке и возвращается.
// Размеры корта — настоящие, в метрах (BWF), проекция — простая центральная:
// экранная координата ∝ 1 / (z + CAMERA_DISTANCE). Геометрия игры (паддл,
// волан, бонусы) через project() не идёт — это только фон, плоские мировые
// координаты её не видят, так что камеру можно крутить не трогая баланс.
//
const COURT = {
  length: 13.4,
  halfDoubles: 3.05,
  halfSingles: 2.59,
  shortService: 1.98,
  longServiceDoubles: 0.76,
  netHeight: 1.55,
}

// Ширина (CAMERA_DISTANCE, PROJ_X_K) подобрана по мокапу (docs/ui/
// gameplay_mockup.jpg): сетка на референсе занимает ~79% ширины экрана,
// ближняя лицевая — ~97%. Высота (PROJ_Y_K, PROJ_Y_0) — своя: на мокапе
// видна только ближняя половина корта (над сеткой сразу небо), а у нас
// нужны обе — дальняя половина за сеткой видна целиком.
//
// COURT_SCALE — общее увеличение корта (по месту просили дважды), но только
// в глубину (PROJ_Y_K): по ширине (PROJ_X_K) расти некуда — ракетка ходит
// в [0, WORLD_WIDTH] (paddle.ts), а ближняя лицевая и так уже ~97% этой
// ширины. С PROJ_X_K, умноженным на COURT_SCALE, корт расширялся дальше
// игрового поля — боковые линии оказывались там, где ракетка физически не
// может оказаться. Якорь по высоте — дальняя лицевая линия (FAR_Y): держит
// отступ под HUD сверху постоянным при любом COURT_SCALE, а ближняя лицевая
// и сетка просто съезжают вниз по мере роста.
const CAMERA_DISTANCE = 28.6
const COURT_SCALE = 1.3
const FAR_Y = 85
const PROJ_X_K = 1643
const PROJ_Y_K = 31375 * COURT_SCALE
const PROJ_Y_0 = FAR_Y - PROJ_Y_K / (COURT.length + CAMERA_DISTANCE)

/**
 * Высота сетки на экране — не по настоящим 1.55 м. На референсе
 * (docs/ui/gameplay_mockup.jpg) замер даёт отношение высоты сетки к её
 * ширине ≈0.32, а настоящие пропорции (1.55 м / 6.1 м корта) дают только
 * ≈0.25: сетку на картинке нарисовали заметно выше, чтобы она читалась —
 * в масштабе честных метров она на экране смотрится слишком приземистой.
 */
const NET_VISUAL_HEIGHT = 1.95

function project(xm: number, zm: number): [number, number] {
  const k = 1 / (zm + CAMERA_DISTANCE)
  return [WORLD_WIDTH / 2 + xm * PROJ_X_K * k, PROJ_Y_0 + PROJ_Y_K * k]
}

function courtLine(ctx: CanvasRenderingContext2D, x1: number, z1: number, x2: number, z2: number) {
  const [ax, ay] = project(x1, z1)
  const [bx, by] = project(x2, z2)
  ctx.moveTo(ax, ay)
  ctx.lineTo(bx, by)
}

/** Контур корта (трапеция в перспективе) — для заливки и для клипа текстуры. Обе половины. */
function courtQuadPath(ctx: CanvasRenderingContext2D): void {
  const { length, halfDoubles: hd } = COURT
  const corners = [project(-hd, 0), project(hd, 0), project(hd, length), project(-hd, length)]
  ctx.beginPath()
  ctx.moveTo(...corners[0])
  for (const c of corners.slice(1)) ctx.lineTo(...c)
  ctx.closePath()
}

function drawCourt(ctx: CanvasRenderingContext2D): void {
  const { length, halfDoubles: hd, halfSingles: hs } = COURT
  const net = length / 2

  // Мягкая тень пола к краям — зал, а не плоская заливка.
  const floorGrad = ctx.createLinearGradient(0, 0, WORLD_WIDTH, 0)
  floorGrad.addColorStop(0, palette.floorShade)
  floorGrad.addColorStop(0.5, palette.floor)
  floorGrad.addColorStop(1, palette.floorShade)
  ctx.fillStyle = floorGrad
  ctx.fillRect(-WORLD_WIDTH, -WORLD_HEIGHT, WORLD_WIDTH * 3, WORLD_HEIGHT * 3)

  const corners = [project(-hd, 0), project(hd, 0), project(hd, length), project(-hd, length)]
  const courtGrad = ctx.createLinearGradient(0, corners[2][1], 0, corners[0][1])
  courtGrad.addColorStop(0, palette.courtFar)
  courtGrad.addColorStop(1, palette.courtNear)
  ctx.fillStyle = courtGrad
  courtQuadPath(ctx)
  ctx.fill()

  ctx.strokeStyle = palette.line
  ctx.lineWidth = 2.2
  ctx.lineCap = 'square'
  ctx.beginPath()
  // Боковые: парные и одиночные, через всю длину — обе половины.
  for (const x of [-hd, -hs, hs, hd]) courtLine(ctx, x, 0, x, length)
  // Лицевые и дальние линии подачи в парном разряде — на обеих половинах.
  for (const z of [0, COURT.longServiceDoubles, length - COURT.longServiceDoubles, length]) {
    courtLine(ctx, -hd, z, hd, z)
  }
  // Передние линии подачи.
  for (const z of [net - COURT.shortService, net + COURT.shortService]) {
    courtLine(ctx, -hd, z, hd, z)
  }
  // Средняя линия — от лицевой до передней линии подачи, у сетки её нет.
  courtLine(ctx, 0, 0, 0, net - COURT.shortService)
  courtLine(ctx, 0, net + COURT.shortService, 0, length)
  ctx.stroke()
}

function drawNet(ctx: CanvasRenderingContext2D): void {
  const z = COURT.length / 2
  const half = COURT.halfDoubles
  const [lx, baseY] = project(-half, z)
  const [rx] = project(half, z)
  // PROJ_X_K заморожен (ширина упирается в WORLD_WIDTH, см. комментарий выше
  // про COURT_SCALE), поэтому высоту сетки растим отдельным множителем —
  // иначе при росте COURT_SCALE сетка осталась бы прежнего размера на фоне
  // подросшего корта.
  const heightPx = NET_VISUAL_HEIGHT * PROJ_X_K * COURT_SCALE * (1 / (z + CAMERA_DISTANCE))
  const topY = baseY - heightPx
  // Сетка висит, до пола не достаёт — замер на референсе (docs/ui/
  // gameplay_mockup.jpg): у сетки есть собственная нижняя кромка (тёмная
  // линия на ~51% высоты от верха), ниже — чистый пол корта до самих стоек.
  const meshBottom = topY + heightPx * 0.51

  // Тень сетки на корте.
  ctx.fillStyle = 'rgba(44, 66, 102, 0.12)'
  ctx.fillRect(lx, baseY - 1, rx - lx, 4)

  const meshPitch = 5 * COURT_SCALE
  ctx.strokeStyle = palette.netMesh
  ctx.lineWidth = 0.7
  ctx.beginPath()
  for (let x = lx; x <= rx; x += meshPitch) {
    ctx.moveTo(x, topY)
    ctx.lineTo(x, meshBottom)
  }
  for (let y = topY; y <= meshBottom; y += meshPitch) {
    ctx.moveTo(lx, y)
    ctx.lineTo(rx, y)
  }
  ctx.stroke()

  ctx.fillStyle = palette.netTape
  ctx.fillRect(lx, topY - 1.5, rx - lx, 3.5)

  // Стойки — шире, чем просто линия, и с раскосом-подпоркой до пола
  // (на референсе ровно так: без него стойка выглядит слишком тонкой и
  // «срезанной» снизу). Раскос уходит наружу, в сторону от корта.
  const postW = 6.5 * COURT_SCALE
  const braceUp = 21 * COURT_SCALE
  const braceReach = 17 * COURT_SCALE
  ctx.strokeStyle = palette.post
  ctx.lineWidth = postW * 0.55
  ctx.lineCap = 'round'
  for (const [x, outward] of [
    [lx, -1],
    [rx, 1],
  ] as const) {
    ctx.beginPath()
    ctx.moveTo(x, baseY - braceUp)
    ctx.lineTo(x + outward * braceReach, baseY)
    ctx.stroke()
  }

  ctx.fillStyle = palette.post
  for (const x of [lx, rx]) {
    ctx.fillRect(x - postW / 2, topY - 3, postW, baseY - topY + 3)
    ctx.beginPath()
    ctx.ellipse(x, baseY, 5 * COURT_SCALE, 1.8 * COURT_SCALE, 0, 0, Math.PI * 2)
    ctx.fill()
  }
}

// ─── Волан ────────────────────────────────────────────────────────────────
//
// Источник — настоящий волан с листа ассетов Gemini (детали обработки —
// в шапке game/shuttle.svg). В игре рисуется не он, а растеризованный из
// него PNG того же каталога — причина та же, что у ракетки (racket.ts):
// Chrome течёт при drawImage() SVG → canvas, когда размер canvas не
// совпадает с размером самого SVG. Рисуется в локальных координатах «ось
// x — направление полёта», пробкой вперёд (на листе волан лежал по
// диагонали, пробкой к верхнему правому углу — довёрнут до горизонтали по
// измеренному методом PCA углу главной оси).

/** Длина волана в мировых единицах, от кончика пера до кончика пробки — унаследована от прежней векторной отрисовки, чтобы не ломать баланс игры. */
const SHUTTLE_LENGTH = 35.75

const SHUTTLE_SPRITE = {
  url: '/game/shuttle.png',
  srcW: 712,
  srcH: 564,
  /** Точка (0,0) локальных координат — «талия» между пробкой и юбкой пера. */
  originX: 469,
  originY: 284,
  /** Полная длина волана в пикселях спрайта — опорная для масштаба. */
  lengthPx: 572,
}

let shuttleSpriteImg: HTMLImageElement | null = null
let shuttleSpriteReady = false
if (typeof Image !== 'undefined') {
  shuttleSpriteImg = new Image()
  shuttleSpriteImg.onload = () => {
    shuttleSpriteReady = true
  }
  shuttleSpriteImg.src = SHUTTLE_SPRITE.url
}

const MM = 0.55

function drawShuttle(ctx: CanvasRenderingContext2D, state: GameState, skin: SkinColors): void {
  const { x, y, vx, vy } = state.shuttle
  const angle = Math.hypot(vx, vy) > 20 ? Math.atan2(vy, vx) : Math.PI / 2
  // Дальше от игрока (выше по экрану) — чуть меньше. Только картинка: зона
  // отбоя от этого не меняется.
  const depth = 0.8 + 0.2 * Math.min(Math.max(y / gameConfig.paddle.y, 0), 1)

  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(angle)
  ctx.scale(depth, depth)
  ctx.shadowColor = 'rgba(30, 50, 80, 0.25)'
  ctx.shadowBlur = 6
  ctx.shadowOffsetY = 3

  if (shuttleSpriteImg && shuttleSpriteReady) {
    const k = SHUTTLE_LENGTH / SHUTTLE_SPRITE.lengthPx
    ctx.drawImage(
      shuttleSpriteImg,
      -SHUTTLE_SPRITE.originX * k,
      -SHUTTLE_SPRITE.originY * k,
      SHUTTLE_SPRITE.srcW * k,
      SHUTTLE_SPRITE.srcH * k,
    )
    ctx.restore()
    return
  }

  // Картинка ещё не загрузилась (первые кадры) — прежняя векторная
  // отрисовка, цвет берём из скина.
  const corkR = 12.5 * MM
  const skirtLen = 45 * MM
  const mouthR = 31 * MM
  const baseR = 11 * MM
  const back = -skirtLen

  // Юбка: 16 перьев, видно ~8. Раструб — с зубчатым краем.
  const skirtGrad = ctx.createLinearGradient(0, -mouthR, 0, mouthR)
  skirtGrad.addColorStop(0, skin.featherShade)
  skirtGrad.addColorStop(0.4, skin.feather)
  skirtGrad.addColorStop(1, skin.featherShade)
  ctx.fillStyle = skirtGrad
  ctx.beginPath()
  ctx.moveTo(0, -baseR)
  ctx.lineTo(back, -mouthR)
  const teeth = 8
  for (let i = 0; i < teeth; i++) {
    const y0 = -mouthR + ((2 * mouthR) / teeth) * i
    const y1 = y0 + (2 * mouthR) / teeth
    ctx.quadraticCurveTo(back - 3.2 * MM * 2, (y0 + y1) / 2, back, y1)
  }
  ctx.lineTo(0, baseR)
  ctx.closePath()
  ctx.fill()
  ctx.shadowColor = 'transparent'

  ctx.strokeStyle = skin.featherShaft
  ctx.lineWidth = 0.7
  ctx.beginPath()
  for (let i = 0; i <= teeth; i++) {
    const t = -1 + (2 * i) / teeth
    ctx.moveTo(0, t * baseR)
    ctx.lineTo(back, t * mouthR)
  }
  ctx.stroke()

  // Две обвязки нитью — на трети и на середине юбки.
  ctx.strokeStyle = skin.thread
  ctx.lineWidth = 1.1
  ctx.beginPath()
  for (const k of [0.3, 0.52]) {
    const px = back * k
    const r = baseR + (mouthR - baseR) * k
    ctx.moveTo(px, -r)
    ctx.lineTo(px, r)
  }
  ctx.stroke()

  // Пробка: цилиндр с лентой + полусфера головки.
  ctx.fillStyle = skin.corkBand
  ctx.fillRect(-1.5 * MM, -corkR * 0.95, 5 * MM, corkR * 1.9)
  const corkGrad = ctx.createRadialGradient(
    corkR * 0.9,
    -corkR * 0.4,
    corkR * 0.1,
    corkR * 0.4,
    0,
    corkR * 1.2,
  )
  corkGrad.addColorStop(0, skin.cork)
  corkGrad.addColorStop(1, skin.corkShade)
  ctx.fillStyle = corkGrad
  ctx.beginPath()
  ctx.moveTo(3.5 * MM, -corkR)
  ctx.lineTo(3.5 * MM + 4 * MM, -corkR)
  ctx.arc(3.5 * MM + 4 * MM, 0, corkR, -Math.PI / 2, Math.PI / 2)
  ctx.lineTo(3.5 * MM, corkR)
  ctx.closePath()
  ctx.fill()

  ctx.restore()
}

function drawParticles(ctx: CanvasRenderingContext2D, state: GameState): void {
  const maxLife = gameConfig.particles.life
  ctx.fillStyle = palette.particle
  for (const p of state.particles) {
    ctx.globalAlpha = Math.max(0, p.life / maxLife) * 0.9
    ctx.beginPath()
    ctx.arc(p.x, p.y, 2, 0, Math.PI * 2)
    ctx.fill()
  }
  ctx.globalAlpha = 1
}

/**
 * Порыв ветра: полупрозрачные штрихи, бегущие по направлению, плюс подпись
 * «Ветер →» — в референсе это не HUD-плашка сверху, а часть рисунка прямо на
 * корте. Появляются и гаснут плавно, чтобы порыв читался, но не отвлекал от
 * волана.
 */
function drawWind(ctx: CanvasRenderingContext2D, force: number, left: number, time: number): void {
  const total = gameConfig.wind.duration
  const fade = Math.min(1, left / 0.4, (total - left) / 0.4)
  const dir = Math.sign(force)
  const span = WORLD_WIDTH + 120
  ctx.strokeStyle = `rgba(255, 255, 255, ${0.5 * fade})`
  ctx.lineWidth = 1.4
  ctx.lineCap = 'round'
  ctx.beginPath()
  for (let i = 0; i < 9; i++) {
    const y = 90 + i * 52 + Math.sin(i * 1.7) * 18
    // Разная скорость у штрихов — иначе они идут строем.
    const run = ((time * (220 + i * 23) + i * 97) % span) - 60
    const x = dir > 0 ? run : WORLD_WIDTH - run
    ctx.moveTo(x, y)
    ctx.lineTo(x - dir * (26 + (i % 3) * 10), y)
  }
  ctx.stroke()

  ctx.fillStyle = `rgba(255, 255, 255, ${0.9 * fade})`
  ctx.font = `600 15px 'Manrope', system-ui, sans-serif`
  ctx.textBaseline = 'middle'
  if (dir > 0) {
    ctx.textAlign = 'left'
    ctx.fillText('Ветер →', WORLD_WIDTH * 0.56, 250)
  } else {
    ctx.textAlign = 'right'
    ctx.fillText('← Ветер', WORLD_WIDTH * 0.44, 250)
  }
}

/** Замедление — прохладный оттенок, ускорение — тёплый: видно, что со временем что-то не так. */
const SLOW_TINT = 'rgba(145, 137, 207, 0.1)'
const FAST_TINT = 'rgba(208, 100, 111, 0.08)'

function drawTint(ctx: CanvasRenderingContext2D, view: View, color: string): void {
  ctx.fillStyle = color
  ctx.fillRect(0, 0, view.width, view.height)
}

/** Промах: экран темнеет, пока идёт замедление. */
function drawMissShade(ctx: CanvasRenderingContext2D, view: View, left: number): void {
  const progress = 1 - Math.max(0, left) / gameConfig.slowMo.duration
  ctx.fillStyle = `rgba(44, 74, 110, ${0.22 * progress})`
  ctx.fillRect(0, 0, view.width, view.height)
}

/** Лёгкое затемнение углов — свет зала падает в центр корта. */
function drawVignette(ctx: CanvasRenderingContext2D, view: View): void {
  const cx = view.width / 2
  const cy = view.height * 0.55
  const r = Math.hypot(view.width, view.height) * 0.6
  const grad = ctx.createRadialGradient(cx, cy, r * 0.35, cx, cy, r)
  grad.addColorStop(0, 'rgba(40, 70, 110, 0)')
  grad.addColorStop(1, 'rgba(40, 70, 110, 0.18)')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, view.width, view.height)
}
